import { useRef, useState } from "react";
import { trackEvent } from "@/hooks/use-analytics";

const fieldClass = "w-full rounded-xl border border-input bg-background p-3 text-sm";
export function SaunaQuoteForm({ service = "Sauna — Install" }: { service?: string }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", postcode: "", type: "Traditional", location: "", project: "", space: "", timeframe: "", message: "", website: "" });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const started = useRef(false);
  const update = (key: keyof typeof form, value: string) => setForm(prev => ({ ...prev, [key]: value }));
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError("");
    trackEvent("sauna_form_submit", "Installation enquiry");
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/contact`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name, email: form.email, phone: form.phone,
          message: form.message || "Sauna installation enquiry",
          formType: "Sauna Project Enquiry",
          saunaEnquiry: { service, postcode: form.postcode, type: form.type, location: form.location, project: form.project, space: form.space, timeframe: form.timeframe, consent },
          website: form.website,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "We could not save your enquiry. Please try again.");
      setSuccess(true);
      trackEvent("sauna_form_success", "Installation enquiry saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Please try again or call 020 8087 0906.");
    } finally { setBusy(false); }
  }
  if (success) return (
    <div role="status" className="rounded-2xl bg-primary/5 border border-primary/20 p-8 text-center">
      <h2 className="text-3xl font-bold font-display mb-3">Sauna enquiry received</h2>
      <p>Your details have been saved for our team to review. We’ll contact you to discuss the next steps.</p>
      <a href={`${import.meta.env.BASE_URL}sauna/install`} className="inline-block mt-6 text-primary underline">Return to sauna installation</a>
    </div>
  );
  return (
    <form onSubmit={submit} onFocusCapture={() => { if (!started.current) { started.current = true; trackEvent("sauna_form_start", "Installation enquiry"); } }} className="space-y-5" data-testid="form-sauna-quote">
      <h2 className="text-3xl font-bold font-display">Tell us about your sauna project</h2>
      <p className="font-medium">{service}</p>
      <p className="text-muted-foreground">UK-wide installation enquiries. Fields marked * are required.</p>
      <div className="grid sm:grid-cols-2 gap-5">
        {([
          ["name", "Full name", "text", "name"],
          ["email", "Email address", "email", "email"],
          ["phone", "Phone number", "tel", "tel"],
          ["postcode", "Postcode", "text", "postal-code"],
        ] as const).map(([key, label, type, autoComplete]) => (
          <div key={key}>
            <label className="block text-sm font-semibold mb-2" htmlFor={`sq-${key}`}>{label} *</label>
            <input id={`sq-${key}`} name={key} type={type} autoComplete={autoComplete} required maxLength={key === "postcode" ? 12 : 150}
              value={form[key]} onChange={e => update(key, e.target.value)} className={fieldClass} />
          </div>
        ))}
        {([
          ["type", "Sauna type", ["Traditional", "Infrared", "Commercial", "Outdoor", "Not sure — please advise"]],
          ["location", "Indoor or outdoor", ["Indoor", "Outdoor / garden", "Not decided"]],
          ["project", "Project type", ["Residential", "Commercial"]],
        ] as const).map(([key, label, options]) => (
          <div key={key}>
            <label htmlFor={`sq-${key}`} className="block text-sm font-semibold mb-2">{label} *</label>
            <select id={`sq-${key}`} required value={form[key]} onChange={e => update(key, e.target.value)} className={fieldClass}>
              <option value="">Please select</option>
              {options.map(option => <option key={option}>{option}</option>)}
            </select>
          </div>
        ))}
        <div>
          <label htmlFor="sq-timeframe" className="block text-sm font-semibold mb-2">Preferred timeframe</label>
          <input id="sq-timeframe" value={form.timeframe} maxLength={200} onChange={e => update("timeframe", e.target.value)} placeholder="For example: within three months" className={fieldClass} />
        </div>
      </div>
      <div>
        <label htmlFor="sq-space" className="block text-sm font-semibold mb-2">Approximate available space</label>
        <input id="sq-space" value={form.space} maxLength={300} onChange={e => update("space", e.target.value)} placeholder="Custom size: width × depth × height, or tell us if unsure" className={fieldClass} />
      </div>
      <div>
        <label htmlFor="sq-message" className="block text-sm font-semibold mb-2">Additional project details</label>
        <textarea id="sq-message" value={form.message} maxLength={5000} onChange={e => update("message", e.target.value)} rows={4} className={fieldClass} />
      </div>
      <div className="absolute -left-[10000px]" aria-hidden="true">
        <label htmlFor="sq-website">Leave this field empty</label>
        <input id="sq-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={e => update("website", e.target.value)} />
      </div>
      <p id="sq-privacy" className="text-sm text-muted-foreground">Vertex Climate uses these details to assess and respond to your enquiry. They are stored in our enquiry system and sent to our team by email; this does not sign you up to marketing. For questions about your information, contact info@vertexclimate.co.uk.</p>
      <label className="flex gap-3 text-sm items-start">
        <input type="checkbox" required checked={consent} onChange={e => setConsent(e.target.checked)} aria-describedby="sq-privacy" className="mt-1" />
        <span>I have read the enquiry privacy notice and agree to be contacted about this project. *</span>
      </label>
      {error && <p role="alert" className="text-red-700 bg-red-50 rounded-lg p-3">{error}</p>}
      <button disabled={busy} type="submit" className="w-full rounded-full bg-primary text-white font-bold px-6 py-4 disabled:opacity-50">{busy ? "Sending enquiry…" : "Request a tailored sauna quote"}</button>
      <p className="text-sm text-center">Prefer to talk? <a className="text-primary underline" href="tel:02080870906" onClick={() => trackEvent("sauna_call_click", "Quote form")}>020 8087 0906</a></p>
    </form>
  );
}
